/* eslint-disable no-undef */
/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useRef } from 'react';
import { Autocomplete, Box, FormControl, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { GetCountries, GetState } from 'react-country-state-city';
import { ButtonComponent, colors, FunctionsEnum, TextFieldComponent } from '@watter/shared-components';
import { userEnums } from '@watter/shared-types';
import { useNavigate } from 'react-router-dom';
import { MainWrapper } from '../common-styles';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { client } from '../../service/apolloClient';
import { ADD_INSTALLATION, UPDATE_INSTALLATION } from '../../graphql/mutations';
import { GET_CUSTOMERS_LIST, GET_INSTALLATION_STATUS_LIST, GET_INSTALLERS_LIST } from '../../graphql/queries';
import 'react-datepicker/dist/react-datepicker.css';
import { usePermissions } from '../../custom_hooks/permissions';
import { NavigationConfig } from '../../utils/navigation-constants';

interface WorkOrderRow {
    id: number;
    installer_name: string;
    installer_user_id?: string;
    customer_id?: string | null;
    customer_name?: number | null;
    address_id?: string | null;
    status: string | number;
    installation_started_at?: string;
    installation_date?: string;
    machine_serial_id?: string | null;
    installation_ended_at?: string;
    agency_id?: string;
    user_status?: number;
    display_date?: any;
    installation_status?: string | null;
}

interface UpdateInstallerProps {
    onClose: () => void;
    installer: WorkOrderRow | null;
    fetchMangeInstallerData: () => void;
    isEdit: boolean;
}

export const AddEditWorkOrder: React.FC<UpdateInstallerProps> = ({
    onClose,
    installer,
    fetchMangeInstallerData,
    isEdit,
}) => {
    const { t } = useTranslation();

    const [customerList, setCustomerList] = useState([]);
    const [installerList, setInstallerList] = useState([]);
    const [customerAddressList, setCustomerAddressList] = useState<any>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string>('');
    const [selectedInstaller, setSelectedInstaller] = useState<string>('');
    const [selectedCustomerAddress, setSelectedCustomerAddress] = useState<string>('');
    const [selectedCustomerAddressID, setSelectedCustomerAddressID] = useState<string>('');
    const [serialID, setSerialID] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | null>();
    const [filteredCountry, setFilteredCountry] = useState<any[]>([]);
    const [stateList, setStateList] = useState([]);
    const [filteredState, setFilteredState] = useState<any[]>([]);
    const dispatch = useDispatch();
    const [customerError, setCustomerError] = useState<string | null>(null);
    const [installerError, setInstallerError] = useState<string | null>(null);
    const [customerPermissionError, setCustomerPermissionError] = useState<string | null>(null);
    const [installerPermissionError, setInstallerPermissionError] = useState<string | null>(null);
    const [serialIDError, setSerialIDError] = useState<string | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [installationStatusList, setInstallationStatusList] = useState<any>([]);
    const [selectedStatus, setSelectedStatus] = useState<any>('');

    const hiddenButtonRef = useRef<HTMLButtonElement | null>(null);
    const filteredInstallers = installerList.filter((inst: any) => inst.status === userEnums.Status.active);
    const { canCreate, canEdit, canView } = usePermissions();
    const navigate = useNavigate();
    const handleInitialData = React.useCallback(() => {
        setSelectedCustomer(installer?.customer_id || '');
        setSelectedInstaller(installer?.installer_user_id || '');
        setSelectedCustomerAddressID(installer?.address_id || '');
        setSerialID(installer?.machine_serial_id || '');
        const date = new Date(installer?.display_date);
        setSelectedDate(date);
        setSelectedStatus(installer?.installation_status);
    }, [installer]);

    const fetchCustomerList = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query<{ getCustomersList: { data: [] } }>({
                query: GET_CUSTOMERS_LIST,
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomersList?.data) {
                const filteredCustomers = data?.getCustomersList?.data.filter(
                    (inst: any) => inst.status === userEnums.Status.active,
                );
                setCustomerList(isEdit ? data?.getCustomersList?.data : filteredCustomers);
                if (isEdit) {
                    const list: any = data?.getCustomersList?.data?.filter((i: any) => i.id === installer?.customer_id);
                    setCustomerAddressList(list?.[0]?.user_addresses);
                }
            } else {
                console.warn('No data found in API response');
            }
        } catch (error: any) {
            console.error('Error fetching data:', error);
            if (error.graphQLErrors && error.graphQLErrors[0]?.extensions?.code === '403') {
                setCustomerPermissionError('You do not have permission to view customer list data.');
            } else {
                // Handle other errors
                console.error('Other error:', error);
            }
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, isEdit, installer?.customer_id]);
    const fetchInstallerList = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query<{ getInstallersList: { data: [] } }>({
                query: GET_INSTALLERS_LIST,
                // variables: {
                //     status: userEnums.Status.active
                // },
                fetchPolicy: 'network-only',
            });

            if (data?.getInstallersList?.data) {
                setInstallerList(data?.getInstallersList?.data);
            } else {
                console.warn('No data found in API response');
            }
        } catch (error: any) {
            console.error('Error fetching data:', error);
            if (error.graphQLErrors && error.graphQLErrors[0]?.extensions?.code === '403') {
                setInstallerPermissionError('You do not have permission to view installer list data.');
            } else {
                // Handle other errors
                console.error('Other error:', error);
            }
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch]);
    const fetchInstallationStatusList = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_INSTALLATION_STATUS_LIST,
                fetchPolicy: 'network-only',
            });

            if (data?.getInstallationStatusList) {
                setInstallationStatusList(data?.getInstallationStatusList);
            } else {
                console.warn('No data found in API response');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch]);
    useEffect(() => {
        if (isEdit) {
            handleInitialData();
            fetchInstallationStatusList();
        }
        fetchInstallerList();
        fetchCustomerList();
    }, [isEdit, handleInitialData, fetchInstallationStatusList, fetchInstallerList, fetchCustomerList]);

    const validateForm = () => {
        let isValid = true;
        setCustomerError(null);
        setInstallerError(null);
        setSerialIDError(null);
        setDateError(null);

        if (!selectedCustomer) {
            setCustomerError(t('please_select_customer'));
            isValid = false;
        }
        if (!selectedInstaller) {
            setInstallerError(t('please_select_installer'));
            isValid = false;
        }
        if (!serialID) {
            setSerialIDError(t('please_enter_serial_id'));
            isValid = false;
        }
        if (!selectedDate) {
            setDateError(t('please_select_installation_date'));
            isValid = false;
        }

        return isValid;
    };

    const handleUpdateInstallerError = (err: any) => {
        console.error('Update Installer failed', err);
        if (err.graphQLErrors && err.graphQLErrors[0]?.extensions?.code === '403') {
            // Handle forbidden: show message, redirect, etc.
            if (canView(FunctionsEnum.MANAGE_WORKORDERS)) {
                navigate(NavigationConfig.ManageWorkOrder);
            } else {
                navigate(NavigationConfig.Home);
            }
            onClose();
        } else {
            // Handle other errors
            console.error('Other error:', err);
        }
    };

    const updateInstallerAPI = async () => {
        try {
            dispatch(showLoader());

            const buildInput = () => {
                const baseInput: any = {
                    customer_id: selectedCustomer || installer?.customer_id,
                    status: selectedStatus || 1,
                    installer_user_id: selectedInstaller || installer?.installer_user_id,
                    installation_date: selectedDate?.toLocaleDateString('en-CA') || installer?.installation_date,
                    agency_id: installer?.agency_id || 1,
                    address_id: selectedCustomerAddressID || installer?.address_id,
                    machine_serial_id: serialID || installer?.machine_serial_id,
                };
                if (isEdit) {
                    baseInput.id = installer?.id;
                }
                return baseInput;
            };

            const input = buildInput();

            const { data } = await client.mutate({
                mutation: isEdit ? UPDATE_INSTALLATION : ADD_INSTALLATION,
                variables: { input },
            });

            dispatch(
                openToast({
                    show: true,
                    message: isEdit ? data?.updateInstallation?.message : data?.addInstallation?.message,
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            onClose();
            fetchMangeInstallerData();
        } catch (err: any) {
            handleUpdateInstallerError(err);
        } finally {
            dispatch(hideLoader());
        }
    };

    const onSubmit = () => {
        if (validateForm()) {
            updateInstallerAPI();
        }
    };
    const handleCustomerChange = (e: any) => {
        setSelectedCustomerAddress('');
        setSelectedCustomer(e?.id);
        const data: any = customerList.filter((i: any) => i.id === e?.id);
        setCustomerAddressList(data[0]?.user_addresses);
    };
    const handleInstallerChange = (e: any) => {
        setSelectedInstaller(e?.id);
    };
    const handleInstallationStatusChange = (e: any) => {
        setSelectedStatus(e?.id);
    };
    const handleDateChange = (date: any) => {
        setSelectedDate(date);
        setIsOpen(false);

        setTimeout(() => {
            hiddenButtonRef.current?.focus();
        }, 100);
    };

    useEffect(() => {
        const fetchFilteredCountry = async () => {
            try {
                const result = await GetCountries();
                const country = customerAddressList && customerAddressList.length && customerAddressList[0]?.country;

                const filterCountry = result.filter((countryObj: any) => countryObj.iso2 === country);
                setFilteredCountry(filterCountry);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        fetchFilteredCountry();
    }, [customerAddressList]);

    useEffect(() => {
        GetState(filteredCountry[0]?.id).then((result: any) => {
            setStateList(result);
        });
    }, [filteredCountry]);

    useEffect(() => {
        const state = customerAddressList && customerAddressList.length && customerAddressList[0]?.state;
        const data: any = stateList.filter((i: any) => i.state_code === state);
        setFilteredState(data[0]?.name);
    }, [stateList, customerAddressList]);

    useEffect(() => {
        if (customerAddressList && customerAddressList?.length > 0 && filteredState && filteredCountry?.length > 0) {
            const address = customerAddressList[0];
            const formattedAddress: any = `${address?.street}, ${address?.city}, ${filteredState}, ${address?.zip_code}, ${filteredCountry[0]?.name}`;
            setSelectedCustomerAddress(formattedAddress);

            setSelectedCustomerAddressID(customerAddressList[0]?.id);
        }
    }, [customerAddressList, filteredState, filteredCountry]);

    const handleIconClick = () => {
        setIsOpen(prev => !prev);
    };
    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const onChangeHandler = (e: any) => {
        let { value } = e.target;
        value = value.replace(/\s+/g, ''); // Allow only alphanumeric characters and hyphens
        setSerialID(value);
    };
    return (
        <Box height="100%">
            <Typography px={4} pt={4} variant="h4">
                {isEdit ? t('edit_work_order') : t('add_work_order')}
            </Typography>
            <MainWrapper className="detailsForm cstWorkOrder">
                <Box>
                    <Box height="calc(100dvh - 136px)" py={1.5} overflow="auto" px={4}>
                        <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <FormControl className="cstSelectProfile cstState">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('installer')}
                                        <Box component="span" color={colors.astric}>
                                            *
                                        </Box>
                                    </Typography>
                                    <Autocomplete
                                        options={filteredInstallers}
                                        getOptionLabel={(option: any) => option.username || ''} // Specify the display label
                                        value={
                                            selectedInstaller
                                                ? installerList.find((inst: any) => inst.id === selectedInstaller) ||
                                                  null
                                                : null
                                        }
                                        onChange={(event, newValue) => {
                                            handleInstallerChange(newValue);
                                        }} // Handle selection
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                placeholder={t('select_installer')}
                                                name="installerName"
                                                InputLabelProps={{ sx: { display: 'none' } }}
                                            />
                                        )}
                                    />

                                    {installerError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {installerError}
                                        </Typography>
                                    )}
                                    {installerPermissionError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {installerPermissionError}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControl className="cstSelectProfile cstState">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('customer')}
                                        <Box component="span" color={colors.astric}>
                                            *
                                        </Box>
                                    </Typography>
                                    <Autocomplete
                                        options={customerList}
                                        getOptionLabel={(option: any) => option.username || ''} // Specify the display label
                                        value={
                                            selectedCustomer
                                                ? customerList.find(
                                                      (customer: any) => customer.id === selectedCustomer,
                                                  ) || null
                                                : ''
                                        }
                                        disabled={isEdit}
                                        onChange={(event, newValue) => {
                                            handleCustomerChange(newValue);
                                        }} // Handle selection
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                placeholder={t('select_customer')}
                                                name="customerName"
                                                InputLabelProps={{ sx: { display: 'none' } }}
                                            />
                                        )}
                                    />
                                    {customerError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {customerError}
                                        </Typography>
                                    )}
                                    {customerPermissionError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {customerPermissionError}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Stack className="cstEmail">
                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                        {t('address')}
                                    </Typography>
                                    <TextFieldComponent
                                        variant="outlined"
                                        required
                                        name="address"
                                        type="text"
                                        placeholder=""
                                        value={selectedCustomerAddress}
                                        className="cstDisableEmail"
                                        disabled
                                    />
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Stack>
                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                        {t('watter_serial_id')}
                                        <Box component="span" color={colors.astric}>
                                            *
                                        </Box>
                                    </Typography>
                                    <TextFieldComponent
                                        variant="outlined"
                                        required
                                        name="serial-id"
                                        type="text"
                                        placeholder=""
                                        onChange={onChangeHandler}
                                        value={serialID}
                                    />
                                    {serialIDError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {serialIDError}
                                        </Typography>
                                    )}
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Stack>
                                    <Typography variant="subtitle1" mb={1} color="info.100">
                                        {t('scheduled_installation_date')}
                                        <Box component="span" color={colors.astric}>
                                            *
                                        </Box>
                                    </Typography>
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={handleDateChange}
                                        showIcon
                                        icon={<CalendarMonthOutlinedIcon onClick={handleIconClick} />}
                                        showPopperArrow={false}
                                        dateFormat="MM/dd/YYYY"
                                        open={isOpen}
                                        onClickOutside={() => setIsOpen(false)}
                                        onFocus={handleInputFocus}
                                        name="scheduled-date"
                                        onBlur={() => setIsOpen(false)}
                                        disabledKeyboardNavigation={!!isEdit}
                                    />
                                    <button
                                        ref={hiddenButtonRef}
                                        style={{ opacity: 0, position: 'absolute' }}
                                        tabIndex={-1}
                                        type="button"
                                        aria-label={t('scheduled_installation_date')}
                                    />
                                    {dateError && (
                                        <Typography color={colors.error} fontSize={12}>
                                            {dateError}
                                        </Typography>
                                    )}
                                </Stack>
                            </Grid>
                            {isEdit && (
                                <Grid size={{ xs: 12 }}>
                                    <FormControl className="cstSelectProfile cstState">
                                        <Typography variant="subtitle1" color="info.100">
                                            {t('instalation_status')}
                                            <Box component="span" color={colors.astric}>
                                                *
                                            </Box>
                                        </Typography>
                                        <Autocomplete
                                            options={installationStatusList}
                                            getOptionLabel={(option: any) => option.description || ''} // Specify the display label
                                            value={
                                                selectedStatus
                                                    ? installationStatusList.find(
                                                          (customer: any) => customer.id === selectedStatus,
                                                      ) || null
                                                    : ''
                                            }
                                            onChange={(event, newValue) => {
                                                handleInstallationStatusChange(newValue);
                                            }} // Handle selection
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    name="status"
                                                    placeholder={t('select')}
                                                    InputLabelProps={{ sx: { display: 'none' } }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems="center"
                        justifyContent="space-between"
                        className="actionFooter"
                        spacing={2}
                        pt={2}
                    >
                        <ButtonComponent
                            size="medium"
                            variant="contained"
                            fullWidth
                            buttonName={t('cancel')}
                            className="cstBtn"
                            onClick={onClose}
                        />
                        <ButtonComponent
                            size="medium"
                            variant="contained"
                            fullWidth
                            buttonName={t('save')}
                            className="cstBtn"
                            onClick={onSubmit}
                            disabled={
                                !(
                                    canCreate(FunctionsEnum.MANAGE_WORKORDERS) ||
                                    canEdit(FunctionsEnum.MANAGE_WORKORDERS)
                                )
                            }
                        />
                    </Stack>
                </Box>
            </MainWrapper>
        </Box>
    );
};
